// src/lib/logger/logger.ts
import { devLogger } from './devLogger';
import { prodLogger } from './prodLogger';

/** pino 호환 로그 레벨 유니온 (LevelWithSilent) */
export const levels = ['fatal', 'error', 'warn', 'info', 'debug', 'trace', 'silent'] as const;
export type Level = (typeof levels)[number];
const isLevel = (s: string): s is Level => (levels as readonly string[]).includes(s);

/** 런타임 환경 */
const isProd = import.meta.env.MODE === 'production';

/** pino 루트 로거 선택 */
const root = isProd ? prodLogger : devLogger;

/** URL/LocalStorage로 레벨 오버라이드 */
(function bootstrapLogLevel() {
  try {
    // URL ?log=debug 지원
    const href = typeof window !== 'undefined' ? window.location.href : '';
    const url = href ? new URL(href) : null;
    const q = url?.searchParams.get('log');
    const stored =
      typeof localStorage !== 'undefined' ? localStorage.getItem('__logLevel__') : null;

    if (q && isLevel(q)) {
      root.level = q;
      try {
        localStorage.setItem('__logLevel__', q);
      } catch {
        /* ignore */
      }
    } else if (stored && isLevel(stored)) {
      root.level = stored;
    }
  } catch {
    // SSR/테스트/프라이빗 모드 등 보호
  }
})();

/** 외부에 노출할 확장 인터페이스 (기존 코드 호환) */
export interface ExtendedLogger {
  debug: (message: string, data?: unknown) => void;
  info: (message: string, data?: unknown) => void;
  warn: (message: string, data?: unknown) => void;
  error: (message: string, error?: unknown) => void;
  time: (label: string) => void;
  timeEnd: (label: string) => void;
  group: (label: string) => void;
  groupEnd: () => void;
  withPrefix: (prefix: string) => ExtendedLogger;
}

/** unknown → pino가 기대하는 object(payload)로 정제 */
function toLogObject(v: unknown): Record<string, unknown> | undefined {
  if (v === undefined) return undefined;
  if (v !== null && typeof v === 'object') return v as Record<string, unknown>;
  // primitive/null을 안전하게 래핑
  return { data: v as unknown };
}

/** 모듈 공용: child 로거로 네임스페이스 관리 */
export function getLogger(namespace: string): ExtendedLogger {
  const childLogger = root.child({ ns: namespace });

  return {
    debug: (message: string, data?: unknown) => {
      const obj = toLogObject(data);
      if (obj)
        childLogger.debug(obj, message); // ✅ object-first
      else childLogger.debug(message);
    },
    info: (message: string, data?: unknown) => {
      const obj = toLogObject(data);
      if (obj) childLogger.info(obj, message);
      else childLogger.info(message);
    },
    warn: (message: string, data?: unknown) => {
      const obj = toLogObject(data);
      if (obj) childLogger.warn(obj, message);
      else childLogger.warn(message);
    },
    error: (message: string, error?: unknown) => {
      // Error 객체면 stack 등 구조화해서 기록
      const obj = error instanceof Error ? { err: error } : toLogObject(error);
      if (obj) childLogger.error(obj, message);
      else childLogger.error(message);
    },
    time: (label: string) => {
      // eslint-disable-next-line no-console
      console.time(`[${namespace}] ${label}`);
    },
    timeEnd: (label: string) => {
      // eslint-disable-next-line no-console
      console.timeEnd(`[${namespace}] ${label}`);
    },
    group: (label: string) => {
      // eslint-disable-next-line no-console
      console.group(`[${namespace}] ${label}`);
    },
    groupEnd: () => {
      // eslint-disable-next-line no-console
      console.groupEnd();
    },
    withPrefix: (prefix: string) => getLogger(`${namespace}:${prefix}`),
  };
}

/** 프로그램 내에서 동적으로 로그 레벨 변경 */
export function setLogLevel(level: Level) {
  root.level = level;
  try {
    localStorage.setItem('__logLevel__', level);
  } catch {
    /* ignore */
  }
}

/** 현재 로그 레벨 조회 */
export function getLogLevel(): Level {
  return root.level as Level;
}

/* ───────────────────────── 추가 팁/가이드 ─────────────────────────
1) 호출 방식:
   - payload가 있으면 반드시 object-first로 호출하세요.
     logger.info('message', { a: 1 })  (X)  → logger.info({ a: 1 }, 'message')  (O)

2) 에러 로깅:
   - Error는 { err }로 감싸서 전달하면 pino가 stack을 구조화합니다.
     logger.error({ err }, 'HWP load failed')

3) 네임스페이스:
   - getLogger('feature:sub') 형태로 기능별/파일별로 쪼개 쓰면 필터링이 쉬워집니다.
   - withPrefix로 세부 태그 추가 가능 (e.g., logger.withPrefix('init'))

4) 런타임 오버라이드:
   - URL ?log=debug, localStorage '__logLevel__'로 레벨 고정/오버라이드 가능합니다.
   - 운영에서는 URL 파라미터 사용을 막고 싶다면 bootstrap에서 q 처리 부분을 제거/주석화하세요.

5) JSON 출력/가독성:
   - 개발(dev)에서는 pino-pretty 등으로 사람이 읽기 좋은 출력 구성,
     운영(prod)에서는 순수 JSON 출력 유지(로그 수집기/관제 연동) 권장.

6) 성능:
   - 불필요한 중첩 객체/거대한 payload는 피하고, 필요한 핵심 필드만 로그에 담으세요.
   - 고빈도 경로는 debug 대신 info/warn/error 중심으로, 샘플링이 필요하면 devLogger/prodLogger에서 구현.

7) 타입 안정성:
   - 외부에서 주는 값은 언제나 unknown 취급 후 toLogObject로 정제.
   - 로깅 궤도에서 any 남발 금지: 지금 패턴을 공용 헬퍼로 강제하는 게 안전합니다.
──────────────────────────────────────────────────────────────── */
